import { Box, Button, Typography } from "@mui/material";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { getGstrReportsClient } from "api/services/gstr/client";
import SearchContainer from "components/SearchContainer";
import Table, { ColumnType } from "components/Table";
import { Permissions } from "data/permissons";
import { useState } from "react";
import { useQuery } from "react-query";
import { ResType } from "types";
import { getGstFinancialYears } from "utils/FinancialYears";
import useTitle from "hooks/useTitle";
import React from "react";
import { SortDirection } from "data/constants";
import _ from "lodash";
import sortHandler from "components/Table/SortHandler";
import { usePermissions } from "context/PermissionsProvider";
import AccessDenied from "views/atom-pro/Access/accessDenide";
import FinancialYearAndMonthSelector from "views/atom-pro/Gstr/modules/Compliance/YearPeriodSelect";
import { PdfReturn } from "api/services/gstr/returnReports";
import ReturnReportsDialog from "views/atom-pro/Gstr/return-reports/ReturnReportsDialog";
import ReturnExcelDialog from "views/atom-pro/Gstr/return-reports/ReturnExcelDialog";
import { placeOfSupplyStates } from "data/placeOfSupply";

// Reports a client row offers (Excel = GST Download page files, PDF = portal "Download Filed"); each kind has its own column
type Report = { label: string; kind: "excel"; ret: string } | { label: string; kind: "pdf"; ret: PdfReturn };
const REPORTS: Report[] = [
  { label: "GSTR-1/IFF Detailed", kind: "excel", ret: "GSTR1" },
  { label: "GSTR-1A Detailed", kind: "excel", ret: "GSTR1A" },
  { label: "GSTR-2A Detailed", kind: "excel", ret: "GSTR2A" },
  { label: "GSTR-2B Detailed", kind: "excel", ret: "GSTR2B" },
  { label: "GSTR-3B Summary", kind: "excel", ret: "GSTR3B" },
  { label: "GSTR-9 Annual Return", kind: "excel", ret: "GSTR9" },
  { label: "GSTR-9C Reconciliation", kind: "excel", ret: "GSTR9C" },
  { label: "GSTR-1/IFF", kind: "pdf", ret: "GSTR1" },
  { label: "GSTR-3B", kind: "pdf", ret: "GSTR3B" },
];

// PDF (portal filed returns) buttons in red tones, Excel (data) buttons in green tones
const reportButtonSx = (kind: Report["kind"]) => {
  const c = kind === "pdf" ? { fg: "#B42318", border: "#F5C2BD", bg: "#FFF8F7", hover: "#FEE4E2" } : { fg: "#146C43", border: "#B7DFC8", bg: "#F6FBF8", hover: "#DDF3E6" };
  return {
    justifyContent: "flex-start",
    gap: 0.5,
    width: "100%",
    minHeight: 26,
    py: 0.125,
    px: 1,
    bgcolor: c.bg,
    color: c.fg,
    border: `1px solid ${c.border}`,
    borderRadius: "6px",
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
    transition: "all .15s ease",
    "& .MuiSvgIcon-root": { fontSize: 15 },
    "&:hover": { bgcolor: c.hover, borderColor: c.fg, boxShadow: "0 2px 6px rgba(15, 23, 42, 0.08)" },
  };
};

// This page only: white rows with a thin divider (instead of the shared table's grey stripes)
const tableSx = {
  borderRadius: "10px",
  border: "1px solid #E2E8F0",
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
  "& table thead th": { px: 2 },                          // header colours = the shared table's (as on other Atom pages)
  "& table tbody tr, & table tbody tr:nth-of-type(odd)": { bgcolor: "#FFFFFF" },
  "& table tbody tr td": { borderBottom: "1px solid #E9EEF5", verticalAlign: "middle", py: 1.5, px: 2 },
  "& table tbody tr td + td": { borderLeft: "1px solid #F1F4F9" },
  "& table tbody tr:hover": { bgcolor: "#F8FAFD" },
  "& table tbody tr:hover td:first-of-type": { boxShadow: "inset 3px 0 0 #1F3A68" },
};

/** "09AAGCT4693M1ZJ" -> "Uttar Pradesh" (state code = first two GSTIN digits). */
const stateOf = (gstin?: string) =>
  placeOfSupplyStates.find((s) => s.startsWith(`${gstin?.slice(0, 2)}-`))?.split("-").slice(1).join("-") ?? "";

/** "APPIAN COMPUTER TECH..." -> "AC" */
const initials = (name?: string) =>
  (name || "?").replace(/[^A-Za-z0-9 ]/g, " ").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

/** "Label value" pair of the client details lines */
const Meta = ({ label, value, wrap }: { label: string; value?: string; wrap?: boolean }) => (
  <Box component="span" sx={{ whiteSpace: wrap ? "normal" : "nowrap" }}>
    <Box component="span" sx={{ color: "#94A3B8", fontSize: "0.72rem", mr: 0.5 }}>
      {label}
    </Box>
    <Box component="span" sx={{ color: "#1E293B", fontSize: "0.8rem", fontWeight: 500 }}>
      {value || "--"}
    </Box>
  </Box>
);

const Dot = () => <Box component="span" sx={{ color: "#CBD5E1", mx: 1 }}>•</Box>;

const ClientDetails = ({ row }: { row: any }) => {
  const gstin = row?.profile?.gstin;
  const name = row?.client?.displayName;
  // GST registration names, each shown only when it adds something (not the client name, not a repeat)
  const same = (a: string, b?: string) => a.toUpperCase() === (b || "").trim().toUpperCase();
  const legalRaw = (row?.profile?.legalName || "").trim();
  const tradeRaw = (row?.profile?.tradeName || "").trim();
  const legal = legalRaw && !same(legalRaw, name) ? legalRaw : "";
  const trade = tradeRaw && !same(tradeRaw, name) && !same(tradeRaw, legalRaw) ? tradeRaw : "";
  return (
    <Box sx={{ width: "100%", minWidth: 380, display: "flex", gap: 1.25 }}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          bgcolor: "#1F3A68",
          color: "#fff",
          fontSize: "0.8rem",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {initials(name)}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, color: "#1F3A68", textTransform: "uppercase", fontSize: "0.9rem", lineHeight: 1.3 }}>
          {name}
        </Typography>
        {(legal || trade) && (
          <Box sx={{ mt: 0.25, lineHeight: 1.6, display: "flex", flexWrap: "wrap", alignItems: "center" }}>
            {legal && <Meta label="Legal Name" value={legal} wrap />}
            {legal && trade && <Dot />}
            {trade && <Meta label="Trade Name" value={trade} wrap />}
          </Box>
        )}
        <Box sx={{ mt: 0.75, lineHeight: 1.7, display: "flex", flexWrap: "wrap", alignItems: "center", rowGap: 0.25 }}>
          <Meta label="GSTIN" value={gstin} />
          <Dot />
          <Meta label="Username" value={row?.userName} />
        </Box>
        <Box sx={{ lineHeight: 1.7, display: "flex", flexWrap: "wrap", alignItems: "center", rowGap: 0.25 }}>
          <Meta label="Client ID" value={row?.client?.clientId} />
          <Dot />
          <Meta label="Type" value={row?.profile?.taxType?.toUpperCase()} />
          <Dot />
          <Meta label="State" value={stateOf(gstin)} />
        </Box>
      </Box>
    </Box>
  );
};

/** One client's report buttons of a kind (PDF or Excel); onOpen opens that return's dialog. */
const Reports = ({ kind, onOpen }: { kind: Report["kind"]; onOpen: (r: Report) => void }) => (
  <Box
    sx={{
      display: "grid",
      // Excel: 7 reports column by column in 3 columns (3 + 3 + 1); PDF: one column; both stretch with the column
      gridTemplateColumns: kind === "excel" ? "repeat(3, minmax(165px, 1fr))" : "minmax(120px, 200px)",
      gridTemplateRows: kind === "excel" ? "repeat(3, auto)" : undefined,
      gridAutoFlow: kind === "excel" ? "column" : "row",
      columnGap: 1,
      rowGap: 0.5,
      py: 0.5,
    }}
  >
    {REPORTS.filter((r) => r.kind === kind).map((r) => (
      <Button
        key={`${r.kind}-${r.ret}`}
        sx={reportButtonSx(r.kind)}
        startIcon={r.kind === "pdf" ? <PictureAsPdfOutlinedIcon /> : <GridOnOutlinedIcon />}
        onClick={() => onOpen(r)}
      >
        {r.label}
      </Button>
    ))}
  </Box>
);

const GstReturnReports = () => {
  useTitle("ATOM Pro | GSTR Returns Reports");
  const financialYears = getGstFinancialYears();
  const [selectedYear, setSelectedYear] = React.useState(financialYears[0]);
  const selectedYearRef = React.useRef(selectedYear);
  selectedYearRef.current = selectedYear;
  const { permissions } = usePermissions();
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(10);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<any>({});
  // client row (a GST login) + return whose month-wise PDFs are open
  const [dialog, setDialog] = useState<{ row: any; ret: PdfReturn; fy: string } | null>(null);
  // client row + return whose Excel download is open
  const [excelDialog, setExcelDialog] = useState<{ row: any; ret: string; fy: string } | null>(null);
  const canView =
    permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) ||
    permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS);

  const { data, isLoading }: ResType = useQuery(
    ["get-gstr-client", { limit: pageCount, offset: page * pageCount, search: search, sort }],
    getGstrReportsClient,
    { enabled: canView }
  );
  const totalCount = data?.data?.count;

  // columns are kept in state (sorting), so the FY is read from the ref at click time
  const openReport = (row: any, r: Report) =>
    r.kind === "pdf"
      ? setDialog({ row, ret: r.ret, fy: selectedYearRef.current })
      : setExcelDialog({ row, ret: r.ret, fy: selectedYearRef.current });

  const defaultColumns: Array<ColumnType> = [
    {
      key: "displayName",
      title: "Client Details",
      width: "40%",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
      render: (v) => <ClientDetails row={v} />,
    },
    {
      key: "id",
      title: "Filed Returns (PDF)",
      width: "15%",
      render: (rowData: any) => <Reports kind="pdf" onOpen={(r) => openReport(rowData, r)} />,
    },
    {
      key: "excel",
      title: "Reports (Excel)",
      width: "45%",
      render: (rowData: any) => <Reports kind="excel" onOpen={(r) => openReport(rowData, r)} />,
    },
  ];
  const [columns, setColumns] = useState(_.cloneDeep(defaultColumns));
  const handleSort = (v: any) => {
    sortHandler({
      key: v.key,
      columns,
      sortState: sort,
      setSortState: setSort,
      setColumns: setColumns,
    });
  };

  if (!canView) {
    return <AccessDenied />;
  }

  return (
    <Box m={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mt={2} mx={1}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1F3A68" }}>GSTR Returns Reports</Typography>
          <Typography variant="body2" sx={{ color: "#64748B" }}>
            Filed return PDFs and return Excels from the GST portal — FY {selectedYear}
            {totalCount ? ` · ${totalCount} GST logins` : ""}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <SearchContainer
            debounced
            value={search}
            placeHolder="Search by Client ID | Client Name | Legal Name | GSTIN"
            onChange={setSearch}
          />
          <FinancialYearAndMonthSelector selectedYear={selectedYear} setSelectedYear={setSelectedYear} />
        </Box>
      </Box>
      <Box m={1} mb={5}>
        <Table
          sx={{ pb: 0, mb: 0, height: "calc(100vh - 220px)", minHeight: "650px", ...tableSx }}
          pagination={{ totalCount, pageCount, setPageCount, page, setPage }}
          data={data?.data?.result || []}
          columns={columns}
          sortHandler={handleSort}
          loading={isLoading}
        />
      </Box>
      {dialog && (
        <ReturnReportsDialog
          open
          onClose={() => setDialog(null)}
          credentialId={dialog.row?.id}
          clientName={dialog.row?.client?.displayName || ""}
          gstin={dialog.row?.profile?.gstin}
          ret={dialog.ret}
          fy={dialog.fy}
        />
      )}
      {excelDialog && (
        <ReturnExcelDialog
          open
          onClose={() => setExcelDialog(null)}
          credentialId={excelDialog.row?.id}
          clientName={excelDialog.row?.client?.displayName || ""}
          gstin={excelDialog.row?.profile?.gstin}
          ret={excelDialog.ret}
          fy={excelDialog.fy}
        />
      )}
    </Box>
  );
};

export default GstReturnReports;
